import * as THREE from 'three';
import {
    CabinetMesh,
    RoomPlannerProductWithTemplate,
} from 'components/customer/RoomPlanner/types';
import {ComputedDimension} from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {autoRotateClamping} from 'components/customer/RoomPlanner/helpers/boundaries/autoRotate';
import {getWallBehindCabinet} from 'components/customer/RoomPlanner/helpers/boundaries/walls';
import {
    getDirection,
    applyBulkheadOffset,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {getCabinetBoundingBox} from 'components/customer/RoomPlanner/helpers/getCabinetBoundingBox';
import {
    determineSpawnLocation,
    applyBulkheadSpawnOffset,
} from 'components/customer/RoomPlanner/helpers/cabinetSpawn';
import {
    getTargetPositionNextToCabinet,
    clearTargetPositioningFlagsIfReady,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {
    getCabinetState,
    getRestorePositionFromHistory,
} from './cabinetGroupUtils';
import {DEFAULT_POSITION_Y} from '../renderCabinetGroup';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';

interface ApplyCabinetPositioningParams {
    cabinetGroup: CabinetMesh;
    product: RoomPlannerProductWithTemplate;
    dimensions: ComputedDimension;
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>;
    roomType: string | undefined;
    positionX: number;
    positionY: number;
    positionZ: number;
    isManualPanelProduct: boolean;
    isKeyShapePanel: boolean;
    isLShapedProduct: boolean;
    isLshapedPanel: boolean;
    isUpperProduct: boolean;
    isFillerProduct: boolean;
    isPanelProduct: boolean;
    isVerminProduct: boolean;
    isBulkheadComponent: boolean;
    extendsToFloor: boolean;
    toeKickHeight: number;
    tempMesh: boolean;
    width: number;
    depth: number;
    height: number;
    scale: number;
}

/**
 * Applies positioning and rotation to a cabinet based on stored state or spawn logic
 */
export const applyCabinetPositioning = ({
    cabinetGroup,
    product,
    dimensions,
    cabinetGroupListRef,
    roomType,
    positionX,
    positionY,
    positionZ,
    isManualPanelProduct,
    isKeyShapePanel,
    isLShapedProduct,
    isLshapedPanel,
    isUpperProduct,
    isFillerProduct,
    isPanelProduct,
    isVerminProduct,
    isBulkheadComponent,
    extendsToFloor,
    toeKickHeight,
    tempMesh,
    depth,
    height,
    scale,
}: ApplyCabinetPositioningParams): void => {
    const {
        position: storedPosition,
        rotation: storedRotation,
        legacyPosition: legacyPosition,
    } = getCabinetState(cabinetGroup);

    const storedCurrentDirection = localStorage.getItem(
        `cabinet-currentDirection:${cabinetGroup.userData.id}`
    );

    if (storedCurrentDirection) {
        cabinetGroup.userData.currentDirection = JSON.parse(
            storedCurrentDirection
        ) as string;
    } else {
        const rotation = (storedRotation as unknown as {_z?: number})?._z;
        if (rotation) {
            const direction = getDirection(rotation);
            const directionMap = {
                normal: 'front',
                right: 'left',
                left: 'right',
                back: 'back',
            };
            // eslint-disable-next-line security/detect-object-injection -- key map as above
            cabinetGroup.userData.currentDirection = directionMap[direction];
        }
    }

    if (storedRotation) {
        cabinetGroup.rotation.copy(storedRotation);
        // For manual panels, ensure X rotation stays at -90° (horizontal)
        if (isManualPanelProduct) {
            cabinetGroup.rotation.x = -(90 * Math.PI) / 180;
        }
    }

    let positionNextToTarget = false;
    let targetCabinet: CabinetMesh | undefined;

    if (storedPosition) {
        cabinetGroup.position.copy(storedPosition);

        if (
            (isFillerProduct || isPanelProduct || isVerminProduct) &&
            !isUpperProduct
        ) {
            let panelPositionY = -(dimensions.wallHeight / 2);
            const positionWithToeKick = panelPositionY + toeKickHeight;
            const positionOnFloor =
                panelPositionY + DEFAULT_POSITION_Y.sitOnFloor - 0.001;
            if (extendsToFloor) {
                panelPositionY = positionOnFloor;
            } else {
                if (cabinetGroup.position.y === positionOnFloor) {
                    // switching from extended to not, so use the default with toekick height
                    panelPositionY = positionWithToeKick;
                } else {
                    // in case distance from floor was updated keep that position
                    panelPositionY = cabinetGroup.position.y;
                }
            }
            cabinetGroup.position.y = panelPositionY;
        }
    } else {
        let spawnPosition: THREE.Vector3;

        if (legacyPosition) {
            const bbox = getCabinetBoundingBox(product);

            /* Tweak dimensions based on the cabinet rotation
             *
             * Legacy dimensions are to the top centre of the cabinet.
             *
             * This should be some trigonometry using sin() and cos(), but we only have to deal with 4 angles, so we're cheating.
             *
             * Amounts were determined experimentally
             */
            switch (Math.round((cabinetGroup.rotation.z * 2) / Math.PI)) {
                case 0:
                    legacyPosition.x -= bbox.width / 2;
                    legacyPosition.y -= bbox.depth / 2;
                    break;
                case 1:
                    legacyPosition.x += bbox.depth / 2;
                    legacyPosition.y -= (3 * bbox.width) / 4;
                    break;
                case 2:
                    legacyPosition.x += bbox.width / 2;
                    legacyPosition.y -= (3 * bbox.depth) / 2;
                    break;
                case -1:
                    legacyPosition.x -= bbox.depth / 2;
                    legacyPosition.y -= (5 * bbox.width) / 4;
                    break;
            }

            // The + 10 keeps cabinets out of the left wall
            spawnPosition = new THREE.Vector3(
                dimensions.computedWidth + 10 + legacyPosition.x * scale,
                positionY,
                positionZ + legacyPosition.y * scale
            );
        } else {
            // Calculate initial position based on cabinet type
            let initialPosition: THREE.Vector3;

            if (!isUpperProduct) {
                if (
                    isFillerProduct ||
                    isPanelProduct ||
                    isVerminProduct ||
                    isKeyShapePanel ||
                    (isLShapedProduct && isLshapedPanel)
                ) {
                    const panelPositionY =
                        -(dimensions.wallHeight / 2) +
                        (isUpperProduct
                            ? 300
                            : extendsToFloor
                              ? DEFAULT_POSITION_Y.sitOnFloor - 0.001
                              : toeKickHeight);
                    cabinetGroup.position.y = panelPositionY;
                    positionY = panelPositionY;
                }

                initialPosition = new THREE.Vector3(
                    positionX,
                    positionY,
                    positionZ
                );
            } else {
                let updatedPositionX = positionX - 0.2;
                if (isBulkheadComponent) {
                    const result = applyBulkheadOffset(
                        updatedPositionX,
                        positionZ,
                        cabinetGroup
                    );

                    updatedPositionX = result.x;
                    positionZ = result.z;
                }

                initialPosition = new THREE.Vector3(
                    updatedPositionX,
                    positionY,
                    positionZ
                );
            }

            // Get existing cabinets. Placeholders count as neighbours so a
            // new cabinet does not spawn on top of a fridge or oven.
            const existingCabinets = cabinetGroupListRef.current;
            const placeholders = productEvents
                .getLayoutMeshes()
                .filter((mesh) => Boolean(mesh.userData.isPlaceholder));
            const spawnNeighbors = [...existingCabinets, ...placeholders];

            // Check if any existing cabinet has positioning flags and calculate position
            const targetPositioning = getTargetPositionNextToCabinet(
                existingCabinets,
                cabinetGroup, // Pass the new cabinet for proper snap calculation
                initialPosition
            );
            targetCabinet = targetPositioning.targetCabinet;
            positionNextToTarget = targetPositioning.positionedNextToTarget;

            if (positionNextToTarget) {
                // Use the calculated snapped position next to target
                spawnPosition = targetPositioning.position;
                if (tempMesh) {
                    spawnPosition.y = spawnPosition.y + (depth / 2) * scale;
                    spawnPosition.z = spawnPosition.z - (depth / 2) * scale;
                }
            } else {
                if (
                    isManualPanelProduct ||
                    isKeyShapePanel ||
                    (isLShapedProduct && isLshapedPanel)
                ) {
                    cabinetGroup.rotation.x = -(90 * Math.PI) / 180;
                    cabinetGroup.scale.set(scale, scale, scale);
                    cabinetGroup.updateMatrixWorld();
                }
                // Use default spawn logic
                spawnPosition = determineSpawnLocation(
                    cabinetGroup,
                    spawnNeighbors,
                    initialPosition,
                    dimensions,
                    roomType
                );

                if (isBulkheadComponent) {
                    applyBulkheadSpawnOffset(
                        spawnPosition,
                        cabinetGroup,
                        scale
                    );
                }

                // NOTE: this is necessary for some reason unknown to me.
                // the cabient and this temp mesh are of same dimension,
                // has same origin and vectors. the determineSpawnLocation
                // also generates the exact same position but for some
                // reason it does not place both mesh in the same spot
                // without this adjustment. If anyone can figure that out
                // please fix this with an explanation. and I will owe you
                // a case of ice cold beer. :D
                if (tempMesh) {
                    spawnPosition.y = spawnPosition.y + (depth / 2) * scale;
                    spawnPosition.z = spawnPosition.z - (height / 2) * scale;
                } else if (isLShapedProduct && isLshapedPanel) {
                    // Adjust base offset (opposite direction) before clamping to wall bounds
                    spawnPosition.y = spawnPosition.y + 0.1;
                    spawnPosition.z = spawnPosition.z + (height / 2) * scale;

                    // Determine wall and clamp so L-shaped products never overlap walls
                    const currentWall = getWallBehindCabinet(
                        cabinetGroup,
                        Camera.scene,
                        roomType
                    ) as THREE.Mesh | null;
                    cabinetGroup.userData.currentWall = currentWall;

                    // Ensure bounding calculations respect final scaling during clamping
                    cabinetGroup.scale.set(scale, scale, scale);
                    ({clamped: spawnPosition} = autoRotateClamping(
                        cabinetGroup,
                        currentWall,
                        spawnPosition.clone(),
                        isUpperProduct,
                        roomType
                    ));
                } else if (isManualPanelProduct || isKeyShapePanel) {
                    // Manual panels also need wall clamping like L-shaped panels
                    if (!isKeyShapePanel)
                        spawnPosition.x +=
                            cabinetGroup.userData.values
                                ?.cabinet_ext_thickness || 16.5;

                    // Sync the cabinet's 3D position to the shifted spawnPosition before
                    // wall detection so getWallBehindCabinet uses the correct location.
                    // Without this, it uses the pre-shift position from determineSpawnLocation
                    // which can be offset by widths accumulated from other product types.
                    cabinetGroup.position.copy(spawnPosition);
                    cabinetGroup.updateMatrixWorld();

                    // Determine wall and clamp so manual panels never overlap walls
                    const currentWall = getWallBehindCabinet(
                        cabinetGroup,
                        Camera.scene,
                        roomType
                    ) as THREE.Mesh | null;
                    cabinetGroup.userData.currentWall = currentWall;

                    // Ensure bounding calculations respect final scaling and rotation during clamping
                    cabinetGroup.updateMatrixWorld();

                    ({clamped: spawnPosition} = autoRotateClamping(
                        cabinetGroup,
                        currentWall,
                        spawnPosition.clone(),
                        false, // isUpperProduct = false for manual panels (they're on the floor)
                        roomType
                    ));
                }
            }
        }

        // If this cabinet is being recreated from a CABINET_ADD history undo,
        // prefer the last saved previousPosition from history (if any) over
        // the automatically computed spawnPosition.
        const restorePosition = getRestorePositionFromHistory(cabinetGroup);

        if (restorePosition) {
            spawnPosition = restorePosition;
        }

        cabinetGroup.position.copy(spawnPosition);

        // Save the position immediately after positioning the new cabinet
        autoSave(cabinetGroup, true);

        // Clean up the positioning flags on the target cabinet after positioning
        if (positionNextToTarget) {
            clearTargetPositioningFlagsIfReady(
                targetCabinet,
                tempMesh,
                product.job_cabinet_id
            );
        }
    }

    // Apply rotation: all cabinets get the X-axis rotation
    // For manual panels, skip if already applied during spawn or from stored rotation
    if (!isManualPanelProduct && !storedRotation) {
        cabinetGroup.rotation.x = -(90 * Math.PI) / 180;
    }
};
